import React from 'react'
import { Button } from './ui'
import { Play, Pause, SkipForward, SkipBack } from 'lucide-react'
import usePlaybackStore from '../stores/playbackStore'
import useQueueStore from '../stores/queueStore'

const PlaybackControls = ({
  currentSong,
  currentTime,
  localIsPlaying,
  audioError,
  audioEngineReady,
  audioTracksLoaded,
  onPlay,
  onPause,
  onNext,
  onPrevious,
  onProgressBarClick,
  formatTime,
  getProgressPercentage,
  queue = [],
  recentUpdate = false,
  controlsVisible = true
}) => {
  // Get store state for optimistic updates
  const { cursor, preloadedSongData } = usePlaybackStore()
  const { getNextSong, getPreviousSong } = useQueueStore.getState()

  const nextItem = getNextSong(queue, cursor)
  const preloadedNext = preloadedSongData?.next
  const preloadedMatches = preloadedNext && nextItem && preloadedNext.id === nextItem.songId

  const nextSong = nextItem
    ? {
      ...nextItem,
      song: {
        ...nextItem.song,
        title: preloadedMatches ? (preloadedNext.title || nextItem.song?.title) : nextItem.song?.title,
        artist: preloadedMatches ? (preloadedNext.artist || nextItem.song?.artist) : nextItem.song?.artist,
      }
    }
    : null

  // Check if there are any previous songs (using cursor-based logic)
  const previousSong = getPreviousSong(queue, cursor)

  // Skip handlers with instant transitions (optimistic updates)
  const handleNextClick = async () => {
    console.log('🎵 Manual next - optimistic transition with preloaded data from cursor:', cursor);
    await onNext(cursor);
  };

  const handlePreviousClick = async () => {
    console.log('🎵 Manual previous - optimistic transition with preloaded data from cursor:', cursor);
    await onPrevious(cursor);
  };

  return (
    <div
      className="px-8 pt-8 flex-shrink-0 relative z-10"
      style={{
        paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))'
      }}
    >
      {/* Progress Bar - fades out on inactivity */}
      {currentSong && (
        <div className={`mb-6 transition-opacity duration-500 ${controlsVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center justify-between text-white/80 text-sm mb-2">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(currentSong.duration || 0)}</span>
          </div>
          <div
            className="w-full bg-white/20 rounded-full h-2 cursor-pointer hover:bg-white/30 transition-colors"
            onClick={onProgressBarClick}
          >
            <div
              className="bg-white rounded-full h-2 transition-all duration-300 pointer-events-none"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
        </div>
      )}

      {/* Controls Container with Up Next */}
      <div className="flex items-center justify-between">
        {/* Left Spacer */}
        <div className="w-64"></div>

        {/* Center Control Buttons - fades out on inactivity */}
        <div className={`flex items-center justify-center space-x-6 transition-opacity duration-500 ${controlsVisible ? 'opacity-100' : 'opacity-0'}`}>
          <Button
            onClick={handlePreviousClick}
            disabled={!previousSong}
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title={previousSong ? "Previous song" : "No previous song"}
          >
            <SkipBack className="w-8 h-8 text-white" />
          </Button>

          <Button
            onClick={localIsPlaying ? onPause : onPlay}
            disabled={!currentSong || audioError || !audioEngineReady || !audioTracksLoaded}
            className="bg-white/30 hover:bg-white/40 backdrop-blur-sm rounded-full p-6 transition-all disabled:opacity-50"
          >
            {localIsPlaying ? (
              <Pause className="w-10 h-10 text-white" />
            ) : (
              <Play className="w-10 h-10 text-white ml-1" />
            )}
          </Button>

          <Button
            onClick={handleNextClick}
            disabled={!nextSong}
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title={nextSong ? "Next song" : "No next song"}
          >
            <SkipForward className="w-8 h-8 text-white" />
          </Button>
        </div>

        {/* Right Spacer - Up Next now handled by UpNextPopup component */}
        <div className="w-64"></div>
      </div>
    </div>
  )
}

export default PlaybackControls
